import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useT } from '@/store';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/ui/Panel';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

/**
 * Ranked driver recommendations for a load, with the reasoning shown.
 *
 * The reasoning is the feature. A bare ordered list of names asks the
 * dispatcher to trust it, and a dispatcher who cannot see why the engine
 * picked someone will either follow it blindly or ignore it entirely — both
 * worse than a list they can argue with. So every row says where the driver
 * is coming from, when they are free, and what it saves.
 *
 * Two honesty markers are deliberately visible rather than tidied away:
 *   - "≈" on an availability that is estimated from an order's stage rather
 *     than observed;
 *   - a note when the distance is straight-line because OSRM was unreachable.
 * The client portal shows eta_source for the same reason: a number presented
 * as measured, when it was guessed, is how a dispatcher promises a truck that
 * cannot arrive.
 *
 * Excluded drivers are shown too, collapsed. "Why isn't Gytis on this list"
 * is the first question a dispatcher asks, and the answer — stale GPS, no
 * hours left — is usually something they need to act on.
 */
export function SmartMatchList({ quarryId, orderId, onPick, pickedDriverId, limit = 5, plannedTons }) {
  const t = useT();

  // V-100. The weight the dispatcher is typing, forwarded so the panel can
  // say whether the truck it is recommending carries this load.
  //
  // Debounced on the same 300 ms as ListPriceNotice, and for a heavier
  // reason than that one: this query ranks the whole fleet and can call the
  // routing service, so a request per keystroke of "24" is three rankings to
  // answer one question. It also stops the verdict flickering through the
  // intermediate numbers a weight passes on its way to being typed.
  //
  // Sent only when the settled value is a number the route will accept,
  // because that route refuses what it cannot read rather than ignoring it —
  // deliberately, so a console bug cannot silently disable an overload
  // warning. Which means the empty and out-of-range states of a weight field
  // must never be sent, and this is where that is decided.
  const settledTons = useDebouncedValue(String(plannedTons ?? ''), 300).trim();
  const tons = settledTons === '' ? NaN : Number(settledTons);
  const tonsParam = Number.isFinite(tons) && tons > 0 && tons <= 100 ? `&planned_tons=${tons}` : '';

  const path = orderId
    ? `/api/orders/${orderId}/smart-match?limit=${limit}${tonsParam}`
    : `/api/quarries/${quarryId}/smart-match?limit=${limit}${tonsParam}`;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['smart-match', orderId ?? quarryId, limit, tonsParam],
    queryFn: () => api(path),
    enabled: Boolean(orderId || quarryId),
    // Positions move and shifts burn down; a recommendation older than this
    // is describing a fleet that has moved on.
    staleTime: 30_000,
  });

  if (!orderId && !quarryId) return null;
  if (isLoading) return <SkeletonRows rows={3} />;
  if (isError) return <ErrorState error={error} />;

  const matches = data?.matches ?? [];
  const excluded = data?.excluded ?? [];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="panel-title !mb-0">{t('smartMatch.title')}</h4>
        {data?.considered ? (
          <span className="text-[11px] text-dim">
            {t('smartMatch.considered', { n: data.considered })}
          </span>
        ) : null}
      </div>

      {matches.length === 0 ? (
        <EmptyState>{t('smartMatch.none')}</EmptyState>
      ) : (
        <ul className="space-y-1.5">
          {matches.map((match, index) => (
            <MatchRow
              key={match.driver_id}
              match={match}
              rank={index + 1}
              onPick={onPick}
              picked={pickedDriverId === match.driver_id}
            />
          ))}
        </ul>
      )}

      {excluded.length > 0 ? (
        <details className="text-[11.5px] text-dim">
          <summary className="cursor-pointer">
            {t('smartMatch.excludedCount', { n: excluded.length })}
          </summary>
          <ul className="mt-1.5 space-y-1">
            {excluded.map((entry) => (
              <li key={entry.driver_id}>
                {entry.full_name} — {t(`smartMatch.excluded.${entry.reason}`)}
                {/* V-85. The plate, when the reason is the truck. "Rimas —
                    truck in the workshop" leaves the dispatcher asking which
                    one; the plate is what he needs to either move him onto
                    another unit or go and close the defect. Nothing else
                    excluded here is about a vehicle, so no other row grows a
                    plate it cannot fill. */}
                {entry.vehicle_plate ? (
                  <span className="ml-1 opacity-80">{entry.vehicle_plate}</span>
                ) : null}
                {/* An exclusion can land on the order's own driver just as
                    easily as on any candidate — a grounded truck reported
                    mid-haul does not touch the order it is already
                    carrying. Same marker the matches list uses above, so
                    "why isn't Rimas on this list" and "wait, that's OUR
                    driver" are answered by the same row. */}
                {entry.is_current_driver ? ` · ${t('smartMatch.currentDriver')}` : ''}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

/**
 * V-84. What the nightly compliance sweep knows, on the screen where the
 * driver is actually chosen.
 *
 * Measured on a live server before this existed: one carrier, one driver,
 * one truck. POST /api/tenants/<t>/compliance-check/run answered "TA
 * pasibaigė prieš 12 dienų" and "95 kodas pasibaigė prieš 3 dienas"; this
 * panel ranked that same driver first of one, said the load fit, and
 * mentioned neither document. The alert went to the owner's phone at 07:00;
 * the dispatcher who sends the truck at 08:00 reads this.
 *
 * ONE LINE PER ROW, never two. `verdict` is already the single worst thing
 * true — expired outranks expiring outranks unknown — so rendering by it
 * gives a row at most one sentence, and a panel of five candidates stays
 * readable. `expired_fields.length` says when there is more behind it.
 *
 * ANNOTATED, NEVER EXCLUDED, and the candidate stays pickable. These dates
 * are typed by hand and go stale the moment a carrier renews a document
 * without opening the console; hiding a driver over one would take a truck
 * off the road for a data-entry lag. A dispatcher who is told can look at
 * the card in the cab. The unattended path — core/auto_dispatch.py, where
 * nobody reads anything — does refuse, and says so in the order history.
 *
 * "Dates not entered" IS rendered, unlike the load-fit warning's `unknown`.
 * The difference is what silence would mean: an unknown capacity says
 * nothing about THIS load, while an unknown expiry date is a roadside risk
 * nobody has measured — and on a fresh install that line is the nudge to
 * fill the fields in. Dim, not red: it is an errand, not a fault.
 */
function ComplianceLine({ state }) {
  const t = useT();
  if (!state || state.verdict === 'valid') return null;

  if (state.verdict === 'unknown') {
    return (
      <p className="text-[10.5px] text-dim">
        {t('smartMatch.docUnknown', { n: state.unknown_fields?.length ?? 0 })}
      </p>
    );
  }

  // The column name resolves to a document name through the bundle rather
  // than through the label the server sends: that label is Lithuanian
  // because its first reader is a Telegram chat, and this console is
  // bilingual. tools/check_i18n.py keeps a key here for every column
  // core/compliance.py watches.
  const doc = t(`document.${state.field}`);

  if (state.verdict === 'expired') {
    const total = state.expired_fields?.length ?? 1;
    return (
      <p className="text-[10.5px] font-semibold text-[#FCA5A5]">
        {t('smartMatch.docExpired', { doc, n: Math.abs(state.days_left) })}
        {total > 1 ? ` · ${t('smartMatch.docAlsoExpired', { n: total })}` : ''}
      </p>
    );
  }

  return (
    <p className="text-[10.5px] text-amber">
      {state.days_left === 0
        ? t('smartMatch.docExpiringToday', { doc })
        : t('smartMatch.docExpiring', { doc, n: state.days_left })}
    </p>
  );
}

/**
 * "Kodėl šis vairuotojas?" — the arithmetic that put this row where it is.
 *
 * The panel has always shown the INPUTS to the ranking (where the driver is
 * coming from, when they are free, what it saves) and never the sum. So a
 * dispatcher could read every fact on a row and still not see why it beat
 * the one below it — which is the position the whole component is written
 * against: a list you cannot argue with is one you either follow blindly or
 * ignore. This is the argument.
 *
 * Four terms, and they are the four the engine actually adds up
 * (core/smart_match.score_components()): travel to the quarry, the wait
 * until this driver is free, the unresolved-findings nudge, and the total.
 * Nothing is invented here for display — a breakdown that does its own
 * arithmetic is a breakdown that can disagree with the ordering it claims
 * to explain.
 *
 * COLLAPSED BY DEFAULT, and that is the point of a <details> rather than
 * four more lines per row. A dispatcher sending a routine load reads the
 * name and the distance; the sum is what they want on the one load where
 * the recommendation looks wrong, and five rows of arithmetic on every
 * screen is how the two lines that matter stop being read.
 *
 * `behind_best_minutes` is the row this panel exists for. "48 min" tells a
 * dispatcher nothing on its own; "+12 min against the top of the list" is
 * the number a decision is actually made on, and it is deliberately absent
 * on rank 1 — the leader's gap against itself is zero, and printing "+0"
 * would read as a measurement rather than as a tautology.
 */
function ScoreBreakdown({ match }) {
  const t = useT();
  const score = match.score;
  // No breakdown at all rather than an empty box: a match from a server
  // that predates this field is not a match with a zero score, and every
  // other honesty marker in this component follows the same rule.
  if (!score || score.total_minutes === null || score.total_minutes === undefined) return null;

  const rows = [
    { key: 'smartMatch.whyDeadhead', value: score.deadhead_minutes },
    { key: 'smartMatch.whyAvailability', value: score.available_in_minutes },
    // Only when it actually moved this candidate. A zero-penalty row on
    // every clean driver is a line that teaches the eye to skip the block.
    ...(score.risk_penalty_minutes > 0
      ? [{ key: 'smartMatch.whyRisk', value: score.risk_penalty_minutes, tone: 'text-amber' }]
      : []),
  ];

  const shiftLeft = match.shift?.remaining_minutes;

  return (
    <details className="mt-1">
      <summary className="cursor-pointer text-[10.5px] text-dim underline decoration-dotted underline-offset-2">
        {t('smartMatch.whyTitle')}
        {score.behind_best_minutes > 0
          ? ` · ${t('smartMatch.whyBehindBest', { n: score.behind_best_minutes })}`
          : ''}
      </summary>
      <dl className="mt-1 space-y-0.5 text-[10.5px]">
        {rows.map((row) => (
          <div key={row.key} className="flex justify-between gap-3">
            <dt className={`text-dim ${row.tone ?? ''}`}>{t(row.key)}</dt>
            <dd className={`tabular-nums ${row.tone ?? ''}`}>
              {t('smartMatch.whyMinutes', { n: row.value })}
            </dd>
          </div>
        ))}
        <div className="flex justify-between gap-3 border-t border-line pt-0.5 font-semibold">
          <dt>{t('smartMatch.whyTotal')}</dt>
          <dd className="tabular-nums">{t('smartMatch.whyMinutes', { n: score.total_minutes })}</dd>
        </div>
      </dl>
      {/* The two facts that did NOT enter the sum but decide whether it can
          be trusted — the shift this job has to fit inside, and how old the
          position it was all measured from is. Both are already bounded by
          the engine's own hard exclusions (a driver who cannot fit the job,
          or whose fix is too old to place, is not on this list at all), so
          they are context, not warnings — dim, and phrased as facts. */}
      <p className="mt-1 text-[10px] text-dim">
        {shiftLeft !== null && shiftLeft !== undefined
          ? t('smartMatch.whyShiftLeft', { n: Math.round(shiftLeft) })
          : null}
        {shiftLeft !== null && shiftLeft !== undefined && match.position_age_minutes !== null
          && match.position_age_minutes !== undefined
          ? ' · '
          : null}
        {match.position_age_minutes !== null && match.position_age_minutes !== undefined
          ? t('smartMatch.whyGpsAge', { n: match.position_age_minutes })
          : null}
      </p>
    </details>
  );
}

function MatchRow({ match, rank, onPick, picked }) {
  const t = useT();
  const pickable = typeof onPick === 'function';

  // The sentence the plan asks for: where they come from, and what it saves.
  const origin =
    match.origin_kind === 'next_dropoff'
      ? t('smartMatch.fromDropoff', { km: match.deadhead_km ?? '—' })
      : t('smartMatch.fromCurrent', { km: match.deadhead_km ?? '—' });

  const availability =
    match.available_in_minutes > 0
      ? t('smartMatch.freeIn', {
          // The tilde is the estimate marker — see the component docstring.
          n: `${match.available_is_estimate ? '≈' : ''}${match.available_in_minutes}`,
        })
      : t('smartMatch.freeNow');

  const body = (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[12.5px] font-semibold">
          {rank}. {match.full_name}
          {match.vehicle_plate ? (
            <span className="ml-1.5 font-normal text-dim">{match.vehicle_plate}</span>
          ) : null}
        </span>
        {match.empty_km_saved_pct > 0 ? (
          <span className="rounded-full bg-green/15 px-2 py-0.5 text-[10px] font-bold text-green">
            {t('smartMatch.savesEmpty', { pct: match.empty_km_saved_pct })}
          </span>
        ) : null}
      </div>
      <p className="mt-0.5 text-[11.5px] text-dim">
        {origin} · {availability}
        {match.is_current_driver ? ` · ${t('smartMatch.currentDriver')}` : ''}
      </p>
      {match.distance_source === 'estimated' ? (
        <p className="text-[10.5px] text-amber">{t('smartMatch.estimatedDistance')}</p>
      ) : null}
      {/* More than this truck is stated to carry.
          NOT "this is illegal" — the product holds no registration mass and
          no axle loads, only the figure the carrier typed for this unit, so
          the strongest true sentence is the one this key says. And the
          candidate is still pickable: the figure may be stale, and a load a
          dispatcher means to split across two runs is ordinary. Rendered
          only for the "over" verdict — "unknown" is the state of every
          carrier who has not entered a capacity, and a line on every row of
          every list is a line nobody reads. */}
      {match.load_fit?.verdict === 'over' ? (
        <p className="text-[10.5px] font-semibold text-[#FCA5A5]">
          {t('smartMatch.overCapacity', {
            over: match.load_fit.over_by_tons,
            capacity: match.load_fit.capacity_tons,
          })}
        </p>
      ) : null}
      {/* V-131. The carrier's OWN maintenance interval, on the screen where
          the truck is chosen. Annotated and still pickable, for the reason
          the capacity line is: this is the carrier's schedule, not a legal
          bar, and a dispatcher who knows the truck went into the workshop
          this morning should not be blocked by a record nobody updated.
          The second sentence says what the unattended path does with it, so
          a carrier reading this row can predict what Auto will do — see
          core/auto_dispatch.py, where an overdue truck is a last resort. */}
      {/* V-131. The carrier's OWN maintenance interval, on the screen where
          the truck is chosen. Annotated and still pickable, for the reason
          the capacity line is: this is the carrier's schedule, not a legal
          bar, and a dispatcher who knows the truck went into the workshop
          this morning should not be blocked by a record nobody updated.
          The second sentence says what the unattended path does with it, so
          a carrier reading this row can predict what Auto will do — see
          core/auto_dispatch.py, where an overdue truck is a last resort. */}
      {match.service_overdue ? (
        <p className="text-[10.5px] font-semibold text-amber">{t('smartMatch.serviceOverdue')}</p>
      ) : null}
      {/* V-301. Not a tonnage comparison like load_fit above — this is
          Valhalla saying no legal road connects the quarry to THIS order's
          site for a truck this heavy. Rendered only for the one verdict
          worth a dispatcher's attention, same "a line nobody reads"
          restraint as load_fit's own comment: "unknown" (no capacity
          entered) and "unavailable" (the routing engine, not the road) are
          both silent here, and "ok" needs no sentence at all. Still
          pickable — annotated, never excluded, same as everything else on
          this row; the dispatcher may know a detour the map doesn't. */}
      {match.route_legality === 'no_legal_route' ? (
        <p className="text-[10.5px] font-semibold text-[#FCA5A5]">
          {t('smartMatch.noLegalRoute')}
        </p>
      ) : null}
      <ComplianceLine state={match.compliance} />
      {match.license_category_warning ? (
        <p className="text-[10.5px] text-amber">
          {t('drivers.licenseCategoryWarning', { category: match.license_category_warning })}
        </p>
      ) : null}
      {/* Last on the row, under every warning: the warnings are things a
          dispatcher must not miss, the arithmetic is what they open when
          they want to disagree with it. */}
      <ScoreBreakdown match={match} />
    </>
  );

  if (!pickable) {
    return <li className="rounded-[10px] border border-line bg-white/2 p-2.5">{body}</li>;
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(match)}
        aria-pressed={picked}
        className={`w-full rounded-[10px] border p-2.5 text-left transition-colors ${
          picked ? 'border-accent/50 bg-accent/10' : 'border-line bg-white/2 hover:bg-white/5'
        }`}
      >
        {body}
      </button>
    </li>
  );
}
